import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useRef,
  useLayoutEffect,
  useSyncExternalStore,
} from 'react';
import { getCartLineAmount } from '../domain/sales/packSellMath';
import { buildCartQtyByPosCardKey } from '../utils/posProductPriceVariants';

/** Stable writers + snapshots — POS catalog can call these without re-rendering on qty changes. */
const CartApiContext = createContext(null);
/** Cart lines + totals — sidebar, checkout UI only (not product grid). */
const CartStateContext = createContext(null);
/**
 * Selective per-card qty store — product cards subscribe with usePosCardCartQty
 * so only badges whose qty changed re-render (not the whole grid).
 */
const CartQtyStoreContext = createContext(null);

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);
  const cartRef = useRef(cart);
  const cartTotalRef = useRef(0);
  const cartItemsCountRef = useRef(0);
  const qtyMapRef = useRef(Object.create(null));
  const qtyListenersRef = useRef(new Set());

  const updateCart = useCallback((newCartOrUpdater) => {
    if (typeof newCartOrUpdater === 'function') {
      setCart(newCartOrUpdater);
    } else {
      setCart(newCartOrUpdater);
    }
  }, []);

  const clearCart = useCallback(() => {
    setCart([]);
  }, []);

  const cartTotal = useMemo(() => {
    return cart.reduce((sum, item) => sum + getCartLineAmount(item), 0);
  }, [cart]);

  const cartItemsCount = useMemo(() => {
    return cart.reduce((sum, item) => sum + (item.quantity || 0), 0);
  }, [cart]);

  cartRef.current = cart;
  cartTotalRef.current = cartTotal;
  cartItemsCountRef.current = cartItemsCount;
  qtyMapRef.current = buildCartQtyByPosCardKey(cart);

  useLayoutEffect(() => {
    qtyListenersRef.current.forEach((listener) => listener());
  }, [cart]);

  const getCart = useCallback(() => cartRef.current, []);
  const getCartTotal = useCallback(() => cartTotalRef.current, []);
  const getCartItemsCount = useCallback(() => cartItemsCountRef.current, []);

  const qtyStore = useMemo(
    () => ({
      subscribe(listener) {
        qtyListenersRef.current.add(listener);
        return () => {
          qtyListenersRef.current.delete(listener);
        };
      },
      getQty(cardKey) {
        const key = String(cardKey || '');
        if (!key) return 0;
        return Number(qtyMapRef.current[key]) || 0;
      },
    }),
    []
  );

  const apiValue = useMemo(
    () => ({
      updateCart,
      clearCart,
      getCart,
      getCartTotal,
      getCartItemsCount,
    }),
    [updateCart, clearCart, getCart, getCartTotal, getCartItemsCount]
  );

  const stateValue = useMemo(
    () => ({
      cart,
      cartTotal,
      cartItemsCount,
    }),
    [cart, cartTotal, cartItemsCount]
  );

  return (
    <CartApiContext.Provider value={apiValue}>
      <CartStateContext.Provider value={stateValue}>
        <CartQtyStoreContext.Provider value={qtyStore}>
          {children}
        </CartQtyStoreContext.Provider>
      </CartStateContext.Provider>
    </CartApiContext.Provider>
  );
};

export const useCartApi = () => {
  const context = useContext(CartApiContext);
  if (!context) {
    throw new Error('useCartApi must be used within a CartProvider');
  }
  return context;
};

export const useCartState = () => {
  const context = useContext(CartStateContext);
  if (!context) {
    throw new Error('useCartState must be used within a CartProvider');
  }
  return context;
};

/**
 * POS product-card badge qty for one card key.
 * Re-renders only when that card's qty changes (useSyncExternalStore + O(1) snapshot).
 */
export const usePosCardCartQty = (cardKey) => {
  const store = useContext(CartQtyStoreContext);
  if (!store) {
    throw new Error('usePosCardCartQty must be used within a CartProvider');
  }
  const key = String(cardKey || '');
  return useSyncExternalStore(
    store.subscribe,
    () => store.getQty(key),
    () => 0
  );
};

/** Full cart (state + writers). Prefer useCartApi / useCartState on hot paths. */
export const useCart = () => {
  const api = useCartApi();
  const state = useCartState();
  return useMemo(() => ({ ...api, ...state }), [api, state]);
};
